import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "h-11 rounded-xl border-slate-200 bg-white";

export default function BookNowButton({ pkg, className = "", testid = "book-now-button", children }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", email: "", travel_date: "", travellers: "2 Adults" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const pay = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/payments/payu/start`, {
        package_slug: pkg.slug,
        package_name: pkg.title,
        origin_url: window.location.origin,
        name: form.name,
        phone: form.phone,
        email: form.email,
        travel_date: form.travel_date,
        travellers: form.travellers,
      });
      const paymentForm = document.createElement("form");
      paymentForm.method = "POST";
      paymentForm.action = data.action;
      Object.entries(data.fields).forEach(([name, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = name;
        input.value = value ?? "";
        paymentForm.appendChild(input);
      });
      document.body.appendChild(paymentForm);
      paymentForm.submit();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start the payment. Please try again or use Get a Quote.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className} data-testid={testid}>
        {children}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="booking-modal" className="max-w-md rounded-2xl max-h-[90vh] overflow-y-auto" data-lenis-prevent>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-bold text-navy">Book with a ₹5,000 Advance</DialogTitle>
            <DialogDescription className="text-slate-500">
              Reserve <span className="font-semibold text-navy">{pkg.title}</span> with a booking advance of ₹5,000 — fully adjustable against your final customised quote.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={pay} className="space-y-4">
            <div>
              <Label htmlFor="bk-name">Full Name *</Label>
              <Input id="bk-name" data-testid="booking-name-input" required minLength={2} placeholder="Your full name"
                     value={form.name} onChange={set("name")} className={inputCls} />
            </div>
            <div>
              <Label htmlFor="bk-phone">Mobile Number *</Label>
              <Input id="bk-phone" data-testid="booking-phone-input" required minLength={8} type="tel" placeholder="+91 ..."
                     value={form.phone} onChange={set("phone")} className={inputCls} />
            </div>
            <div>
              <Label htmlFor="bk-email">Email *</Label>
              <Input id="bk-email" data-testid="booking-email-input" required type="email" placeholder="you@example.com"
                     value={form.email} onChange={set("email")} className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="bk-date">Travel Date</Label>
                <Input id="bk-date" data-testid="booking-date-input" type="date"
                       value={form.travel_date} onChange={set("travel_date")} className={inputCls} />
              </div>
              <div>
                <Label htmlFor="bk-trav">Travellers</Label>
                <Input id="bk-trav" data-testid="booking-travellers-input" placeholder="2 Adults, 1 Child"
                       value={form.travellers} onChange={set("travellers")} className={inputCls} />
              </div>
            </div>
            <Button type="submit" disabled={submitting} data-testid="booking-pay-button"
                    className="w-full h-12 rounded-full bg-gold text-navydeep font-heading font-bold hover:bg-amber-400 transition-colors">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : "PAY ₹5,000 & RESERVE"}
            </Button>
            <p className="flex items-center justify-center gap-1.5 text-xs text-slate-400">
              <ShieldCheck className="w-3.5 h-3.5" /> Secure card / UPI / netbanking payment powered by PayU.
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
